import { useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { assessmentQuery } from '../api/dnscAssessment'
import { nis2Query } from '../api/overview'
import { moduleOf, shellQuery } from '../api/shell'
import { Content } from '../ui/Shell'
import { usePageStyles } from '../ui/usePageStyles'
import { formatNumber, scoreTone } from './format'
import { hrefOf } from './href'
import { NIS2_WINDOW_DAYS, nis2Letters, nis2Overall } from './letters'
import { Nis2Assessment } from './Nis2Assessment'
import { Nis2Dnsc } from './Nis2Dnsc'
import { Nis2Summary } from './Nis2Summary'

/**
 * Overview::nis2 - macheta nis2/index.php, litera cu litera.
 * GET /api/v2/overview/nis2. Fara Easeedesk in v4; (b) duce in portal.
 */

const STYLES = ['css/adm.css', 'css/overview_nis2.css', 'css/overview_nis2_dnsc.css', 'css/overview_nis2_assessment.css', 'css/overview_nis2_summary.css']

/*
 * Doua file (16 octombrie 2026): literele Art. 21(2) - ecranul de pana acum,
 * neschimbat - si autoevaluarea DNSC, care se citeste numai cand se deschide
 * (are dreptul ei). `?tab=dnsc` deschide direct a doua fila.
 * 3 noiembrie 2026: Summary e prima si se deschide fara parametru; literele cu `?tab=letters`.
 */
type Nis2Tab = 'summary' | 'letters' | 'dnsc'

function tabInUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('tab')
  } catch {
    return null
  }
}

function tabFromUrl(): Nis2Tab {
  const tab = tabInUrl()
  return tab === 'dnsc' || tab === 'letters' ? tab : 'summary'
}

/** Adresa filei; `req` = cerinta pe care o deschide fila DNSC (butonul Answer din Summary). */
function tabHref(tab: Nis2Tab, req: string | null): string {
  const url = new URL(window.location.href)
  if (tab === 'summary') url.searchParams.delete('tab')
  else url.searchParams.set('tab', tab)
  if (req) url.searchParams.set('req', req)
  else url.searchParams.delete('req')
  return url.pathname + url.search + url.hash
}

function rememberTab(tab: Nis2Tab, req: string | null = null): void {
  try {
    window.history.replaceState(window.history.state, '', tabHref(tab, req))
  } catch {
    /* fara istoric (proba, cadru): fila ramane doar in memorie */
  }
}

export function Nis2Page() {
  const shell = useQuery(shellQuery(moduleOf('/overview/nis2')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const q = useQuery(nis2Query)
  const page = (q.data ?? {}) as Record<string, unknown>
  const fleet = (page.fleet ?? {}) as Record<string, unknown>
  const letters = q.data ? nis2Letters(page) : []
  const { overall, measured, manual } = nis2Overall(letters)
  const [tab, setTab] = useState<Nis2Tab>(tabFromUrl)
  /* Fila aleasa (din adresa sau de mana); altfel Summary e doar implicita. */
  const [chosen, setChosen] = useState(() => tabInUrl() !== null)
  const right = useQuery({ ...assessmentQuery, enabled: tab === 'summary' && !chosen })
  const noRight = !chosen && right.error instanceof ApiError && right.error.status === 403
  /* Fara dreptul autoevaluarii, implicit raman literele, nu un lacat. */
  const shown: Nis2Tab = tab === 'summary' && noRight ? 'letters' : tab
  const router = useRouter({ warn: false })
  const open = (next: Nis2Tab, req: string | null = null) => {
    setChosen(true)
    /*
     * Routerul aude orice replaceState si duce pagina sus dupa ce deseneaza;
     * pe Answer trecem prin el fara asta, ca randul cerut sa ramana in vedere.
     */
    if (req && router) {
      void router.navigate({ href: tabHref(next, req), replace: true, resetScroll: false }).then(() => setTab(next))
      return
    }
    rememberTab(next, req)
    setTab(next)
  }

  return (
    <Content>
      <div className="adm-page-head adm-tone-green-mode">
        <span className="adm-page-icon">
          <i className="fas fa-shield-alt"></i>
        </span>
        <div className="adm-page-title">
          <div className="adm-page-title-line">
            <h3>NIS2 - Art. 21(2)</h3>
          </div>
          <span className="adm-page-note">
            the evidence, letter by letter: what is measured now, and what is left to write into policy
          </span>
        </div>
        <div className="adm-page-stats">
          <div className="adm-stat adm-stat-green-mode">
            <span className="adm-stat-value">{overall === null ? '—' : `${overall}%`}</span>
            <span className="adm-stat-label">score across measured letters</span>
          </div>
          <div className="adm-stat adm-stat-blue-mauve">
            <span className="adm-stat-value">
              {measured} / {letters.length || 10}
            </span>
            <span className="adm-stat-label">letters backed by data</span>
          </div>
          <div className="adm-stat adm-stat-yellow">
            <span className="adm-stat-value">{manual}</span>
            <span className="adm-stat-label">to document in policy</span>
          </div>
          <div className="adm-stat adm-stat-blue-hoki">
            <span className="adm-stat-value">{formatNumber(Number(fleet.seen) || 0)}</span>
            <span className="adm-stat-label">machines active in {NIS2_WINDOW_DAYS} days</span>
          </div>
        </div>
      </div>

      <ul className="nav nav-tabs line-tabs nis2-tabs" role="tablist">
        {(
          [
            ['summary', 'Summary', 'fas fa-clipboard-list'],
            ['letters', 'Art. 21(2) letters', 'fas fa-list-ol'],
            ['dnsc', 'DNSC self-assessment', 'fas fa-clipboard-check'],
          ] as Array<[Nis2Tab, string, string]>
        ).map(([key, label, icon]) => (
          <li key={key} role="presentation" className={shown === key ? 'active' : undefined}>
            <a
              role="tab"
              tabIndex={0}
              aria-selected={shown === key}
              data-nis2-tab={key}
              onClick={() => open(key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  open(key)
                }
              }}
            >
              <i className={icon} aria-hidden="true"></i> {label}
            </a>
          </li>
        ))}
      </ul>

      {shown === 'summary' ? <Nis2Summary onOpenDnsc={(code) => open('dnsc', code)} /> : null}

      {shown === 'dnsc' ? <Nis2Assessment /> : null}

      {shown === 'letters' ? (
        <>
          <div className="adm-card nis2-intro">
            <i className="fas fa-info-circle"></i>
            Each letter below is one requirement of NIS2 Art. 21(2). The percentages come from numbers you can open and count - not
            from statements. A letter without a percentage is neither met nor failed: its evidence is a written policy, and the
            screen says where to write it.
          </div>

          {letters.map((letter) => {
            const href = letter.link ? hrefOf(letter.link) : null
            const tone = scoreTone(letter.score)
            return (
              <div className={`adm-card nis2-letter nis2-letter-${letter.state}`} key={letter.key} data-letter={letter.key}>
                <div className="nis2-letter-badge">({letter.key})</div>
                <div className="nis2-letter-body">
                  <h4>{letter.name}</h4>
                  <p className="nis2-letter-asks">{letter.asks}</p>
                  {letter.state === 'off' ? (
                    <p className="nis2-evidence nis2-muted">
                      <i className="fas fa-database"></i>{' '}
                      {letter.off_reason ?? 'the module that measures this letter is not switched on in this instance'}
                    </p>
                  ) : (
                    <table className="nis2-evidence">
                      <tbody>
                        {letter.evidence.map((row) => (
                          <tr key={row.label}>
                            <td>{row.label}</td>
                            <td className={`nis2-value${row.bad ? ' nis2-bad' : ''}`}>{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="nis2-letter-score">
                  {letter.score !== null ? (
                    <>
                      <div className={`nis2-score nis2-score-${tone}`}>{letter.score}%</div>
                      <div className="nis2-bar">
                        <div className={`nis2-bar-fill nis2-bar-${tone}`} style={{ width: `${letter.score}%` }}></div>
                      </div>
                    </>
                  ) : letter.state === 'manual' ? (
                    <span className="nis2-pill nis2-pill-manual">to document</span>
                  ) : letter.state === 'off' ? (
                    <span className="nis2-pill nis2-pill-off">{letter.off_reason ? 'not measured' : 'module off'}</span>
                  ) : (
                    <span className="nis2-pill nis2-pill-off">no data yet</span>
                  )}
                  {href && letter.link_label ? (
                    <a className="nis2-open" href={href}>
                      {letter.link_label} <i className="fa fa-arrow-circle-right"></i>
                    </a>
                  ) : null}
                </div>
              </div>
            )
          })}

          <Nis2Dnsc />

          <p className="nis2-foot adm-muted">
            The incident and change figures are the same ones shown on the <a href={hrefOf('overview/dashboard') ?? '/app/overview/dashboard'}>ITIL board</a> - one
            answer to a question, whichever screen asks it.
          </p>
        </>
      ) : null}
    </Content>
  )
}
